import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import styles from "./PlanDetail.module.css";
import Api from "../../utils/Api/Api";
import Helper from "../../utils/Helper";
import { useSeo } from "../../utils/useSeo";
import { sanitizeBrandPayload, buildBrandPage, atBrandPrice } from "../../seo/brandPage";
import { money, longDate } from "../../utils/format";
import { useAuthGate, hasSession } from "../../components/AuthModal/AuthGate";
import { ErrorFallback, createErrorDetails } from "../../components/Error/Error";
import { TopBar } from "../../components/TopBar/TopBar";
import { CtaBar } from "../../components/CtaBar/CtaBar";
import { Sheet } from "../../components/Sheet/Sheet";
import { GiftSheet } from "../../components/GiftSheet/GiftSheet";
import { track, EVENTS } from "../../utils/analyticsService";
import { useGiftEnabled } from "../../utils/giftGate";
import { saveGiftDraft, clearGiftDraft, firstName } from "../../utils/gift";
import { loadBestsellers, badgeRank, shouldShowStrip } from "../../utils/bestsellers";
import coinLogo from "../../asset/icons/fleek-coin-logo.png";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";
import { useAdCoupons, adCouponFor } from "../../utils/adCoupon";
import { isIndia, noteStoreCurrency } from "../../utils/market";
import { isInr } from "../../utils/currency";

// Chess.com predates affiliate plans in the catalogue: its brand page still
// links out by hand until its plans carry the affiliate flag.
const CHESS_AFFILIATE_URL = "https://chess.com/membership?ref_id=372424947";

// A plan sold on the brand's own site (backend marketplace.affiliate): the buy
// button sends the member there through their own tracking link, never the
// cart, and Fleek takes no payment.
const isAffiliate = (p) => p?.affiliate === true;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// App's axios interceptor resolves a failed request with the error.
const failed = (res) => !res || res instanceof Error || !!res.isAxiosError;

const GiftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 12v9H4v-9" />
    <path d="M2 7h20v5H2z" />
    <path d="M12 21V7" />
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
);

// The line under the plans for a plan bought on the brand's own site. Its
// price is already the brand's own, in the brand's currency (atBrandPrice).
const affiliateNote = (name) => `Bought on ${name}'s own site: you'll sign up and pay ${name} directly.`;

const lines = (text) => String(text || "").split("\n").map((l) => l.trim()).filter(Boolean);

// The brand page's state, from a get_digital_plans_from_vendor payload (live,
// or the sanitized seed the prerendered HTML carries).
const toBrand = (payload, vendorName) => {
  const vendor = payload.vendor_data || {};
  const service = vendor.service || {};
  return {
    raw: payload,
    id: vendor.vendor_id,
    name: service.service_name || "",
    category: service.category || "",
    logo: service.logo_url || "",
    banner: vendor.detail_page_banner_url || "",
    desc: vendor.description || "",
    plans: (payload.plans_data || payload.plans || []).map(atBrandPrice),
    benefits: lines(vendor.benefits),
    isVerified: vendor.is_verified === true,
    activeSubscription: !!vendor.is_already_availed_in_subscribe_flow,
    dueDate: vendor.due_date,
    extraDetails: Array.isArray(vendor.extra_details_required) ? vendor.extra_details_required : [],
    ageGate: !!vendor.show_age_restrictions_popup,
  };
};

// scripts/prerender-meta.js embeds the payload the page was built from, so the
// first render is the brand page itself and not a skeleton replacing the
// static content. The live fetch still runs and replaces it.
// "More <category> on Fleek": the category hub and its leading brands, written
// by the prerender next to the seed (scripts/prerender-meta.js), so the
// rendered page links onward exactly as the static HTML does.
const readRelated = (vendorName) => {
  try {
    const el = typeof document !== "undefined" && document.getElementById("brand-related");
    if (!el) return null;
    const related = JSON.parse(el.textContent || "null");
    if (!related || related.slug !== vendorName) return null;
    const brands = Array.isArray(related.brands) ? related.brands.filter((b) => b && b.slug && b.name) : [];
    const category = related.category && related.category.slug ? related.category : null;
    return brands.length || category ? { brands, category } : null;
  } catch {
    return null;
  }
};

// A store's own page (/us/brand/steam) carries its country on the seed;
// India's carries none. The seed is only this page's when both match.
const readSeed = (vendorName, country) => {
  try {
    const el = typeof document !== "undefined" && document.getElementById("brand-seed");
    if (!el) return null;
    const seed = JSON.parse(el.textContent || "null");
    const sameStore = (seed?.country || "") === String(country || "").toUpperCase();
    return seed && sameStore && seed.slug === vendorName && seed.payload?.vendor_data?.service?.service_name ? seed.payload : null;
  } catch {
    return null;
  }
};

/* ---- Sheets ---- */

const NoticeSheet = ({ notice, onClose }) => (
  <Sheet open={!!notice} onClose={notice?.dismissible ? onClose : () => {}} dismissible={!!notice?.dismissible} label={notice?.title}>
    <p className={styles.sheetTitle}>{notice?.title}</p>
    {notice?.body && <p className={styles.sheetBody}>{notice.body}</p>}
    {notice?.primary && (
      <button type="button" className={styles.sheetPrimary} onClick={notice.primary.onClick}>
        {notice.primary.text}
      </button>
    )}
    {notice?.secondary && (
      <button type="button" className={styles.sheetGhost} onClick={notice.secondary.onClick}>
        {notice.secondary.text}
      </button>
    )}
  </Sheet>
);

const ExtraDetailsSheet = ({ open, fields, onClose, onSubmit }) => {
  const [values, setValues] = useState({});
  useEffect(() => {
    if (open) setValues({});
  }, [open]);
  const complete = fields.every((f) => String(values[f.json_key] || "").trim());
  return (
    <Sheet open={open} onClose={onClose} label="Provide the details">
      <p className={styles.sheetTitle}>Provide the details</p>
      <p className={styles.sheetBody}>To activate the subscription</p>
      <div className={styles.sheetFields}>
        {fields.map((f) => (
          <div key={f.json_key}>
            <label className={styles.sheetLabel} htmlFor={`extra-${f.json_key}`}>{f.display_text}</label>
            <div className={styles.sheetField}>
              <input
                id={`extra-${f.json_key}`}
                className={styles.sheetInput}
                type={f.type || "text"}
                value={values[f.json_key] || ""}
                onChange={(e) => setValues((v) => ({ ...v, [f.json_key]: e.target.value }))}
              />
            </div>
          </div>
        ))}
      </div>
      <button type="button" className={styles.sheetPrimary} disabled={!complete} onClick={() => onSubmit(values)}>
        Proceed
      </button>
    </Sheet>
  );
};

/**
 * Plan detail, rebuilt from design/Purchase Flow.dc.html (isPlan block).
 */
export const PlanDetail = () => {
  const navigate = useNavigate();
  // country: a store's own brand page, /us/brand/steam (WebSiteRoutes).
  const { vendor_name, country } = useParams();
  const { requireAuth } = useAuthGate();

  const [seed] = useState(() => readSeed(vendor_name, country));
  const related = useMemo(() => readRelated(vendor_name), [vendor_name]);
  const [brand, setBrand] = useState(() => (seed ? toBrand(seed, vendor_name) : null));
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [openFaq, setOpenFaq] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!seed);
  // Buying waits for the live payload (user-specific flags such as an
  // already-availed plan only come from it).
  const [live, setLive] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [notice, setNotice] = useState(null);
  const [extraOpen, setExtraOpen] = useState(false);
  const [pendingDetails, setPendingDetails] = useState(null);
  const giftEnabled = useGiftEnabled();
  const [giftOpen, setGiftOpen] = useState(false);
  // An ad link's code, shown as the saving on verified plans (utils/adCoupon.js).
  const adConfig = useAdCoupons();
  const [giftSender, setGiftSender] = useState("");
  const [giftBusy, setGiftBusy] = useState(false);
  const [giftError, setGiftError] = useState("");
  // Session-cached and non-blocking; only decides the header's Bestseller chip
  // (top bestsellers only; a Fleek pick never gets it).
  const [bestsellers, setBestsellers] = useState([]);

  /* ---- Load ---- */

  useEffect(() => {
    let cancelled = false;
    const seeded = readSeed(vendor_name, country);
    setLive(false);
    if (seeded) {
      setBrand((b) => (b && b.raw === seeded ? b : toBrand(seeded, vendor_name)));
      setLoading(false);
    } else {
      setLoading(true);
    }
    (async () => {
      try {
        const res = await Api.BrandDetails({ params: { vendor_name, show_out_of_stock: true, extra_details_for_web: true } });
        const payload = res?.data?.data || res?.data || {};
        const vendor = payload.vendor_data || {};
        if (cancelled) return;
        const service = vendor.service || {};
        if (!service.service_name && seeded) {
          // Keep the prerendered brand; the cart API checks the plan anyway.
          setLive(true);
          return;
        }
        setBrand(toBrand(payload, vendor_name));
        setLive(true);
        // product_viewed with the plan the page preselects (first buyable).
        const shown = (payload.plans_data || payload.plans || []).filter(
          (p) => !(!isInr(p.currency_type) && p.is_out_of_stock)
        );
        const lead = shown.find((p) => !(p.is_out_of_stock || p.already_availed)) || shown[0];
        track(EVENTS.PRODUCT_VIEWED, {
          plan_id: lead?.subscription_plan_id ?? null,
          plan_name: lead?.plan_name || null,
          brand: service.service_name || vendor_name,
          price: num(lead?.discounted_cost),
          currency: String(lead?.currency_type || "inr").toUpperCase(),
          product_type: "plan",
          plan_count: shown.length,
        });
      } catch (err) {
        if (cancelled) return;
        setErrorDetails(
          createErrorDetails(err, {
            componentName: "PlanDetail",
            errorContext: "brand_details_fetch",
            apiCallsAttempted: ["BrandDetails"],
            additionalContext: { vendor_name },
          })
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vendor_name, country]);

  useEffect(() => {
    let cancelled = false;
    loadBestsellers().then((list) => {
      // Too few to show the strip is too few to reorder or badge by, too.
      if (!cancelled) setBestsellers(shouldShowStrip(list) ? list : []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---- Derived ---- */

  const bestsellerRank = badgeRank(bestsellers, { vendorId: brand?.id, vendorWebPath: vendor_name });

  // Plans outside India (any currency but rupees) that are out of stock are
  // hidden outright, not disabled.
  const plans = useMemo(
    () => (brand?.plans || []).filter((p) => !(!isInr(p.currency_type) && p.is_out_of_stock)),
    [brand]
  );
  const isBlocked = (p) => Boolean(p.is_out_of_stock || p.already_availed);

  useEffect(() => {
    if (selectedIdx !== -1 || !plans.length) return;
    const first = plans.findIndex((p) => !isBlocked(p));
    if (first !== -1) setSelectedIdx(first);
  }, [plans, selectedIdx]);

  const selected = plans[selectedIdx];

  useEffect(() => {
    if (selected?.currency_type) {
      Helper.checkAndHandleUSDCurrency(selected.currency_type);
      // The store picker and notice say the currency this store's prices are
      // in; an affiliate plan is priced in its brand's currency, not the store's.
      if (!isAffiliate(selected)) noteStoreCurrency(selected.currency_type);
    }
  }, [selected]);

  /* ---- Age gate: asked once, before anything else ---- */

  useEffect(() => {
    if (!brand?.ageGate) return;
    setNotice({
      title: "You need to be above 18 years of age",
      body: "Disclaimer: This subscription may contain adult language and content. It is not suitable for anyone under the age of 18 and may not be suitable for all adult viewers, view at your discretion.",
      dismissible: false,
      primary: {
        text: "Yes, I am above 18",
        onClick: async () => {
          try {
            await Api.AgeRestrictionUpdate({ vendor: brand.id });
          } catch {
            /* logging only */
          }
          setNotice(null);
        },
      },
      secondary: { text: "No, I'm not", onClick: () => navigate("/marketplace") },
    });
  }, [brand, navigate]);

  /* ---- SEO ---- */

  // Title, description, FAQ and JSON-LD come from the same builder the
  // prerender uses, over the same plans, so they can never disagree.
  const page = useMemo(
    () => (brand?.name ? buildBrandPage(vendor_name, sanitizeBrandPayload(brand.raw), { country }) : null),
    [brand, vendor_name, country]
  );

  useSeo({
    title: page?.title,
    description: page?.metaDescription,
    canonical: page?.url,
    image: page?.image || undefined,
    jsonLd: page?.jsonLd,
  });

  /* ---- Buy ---- */

  // Hands a signed-in member over to the brand for an affiliate plan. Called
  // from a click: the tab is opened before the link is fetched, so a popup
  // blocker sees it come from the click, and is pointed at the brand once the
  // link arrives. With no tab (blocked anyway) this tab goes instead.
  const goToBrand = useCallback(
    async (plan) => {
      if (!plan) return;
      const tab = window.open("", "_blank");
      if (tab) {
        try {
          tab.opener = null;
        } catch {
          /* already cut off */
        }
      }
      setBusy(true);
      const res = await Api.AffiliateLink(plan.subscription_plan_id).catch((e) => e);
      setBusy(false);
      const url = failed(res) ? "" : String(res?.data?.url || "");
      if (!/^https:\/\//i.test(url)) {
        if (tab) tab.close();
        setNotice({
          title: `We couldn't open ${brand?.name || "the brand's site"}`,
          body: "Please try again in a moment.",
          dismissible: true,
          primary: { text: "Okay", onClick: () => setNotice(null) },
        });
        return;
      }
      if (tab) tab.location.replace(url);
      else window.location.assign(url);
    },
    [brand]
  );

  const buy = useCallback(
    async (details) => {
      if (!selected || busy) return;

      if (isAffiliate(selected)) {
        track(EVENTS.AFFILIATE_CLICKED, {
          plan_id: selected.subscription_plan_id ?? null,
          plan_name: selected.plan_name || null,
          brand: brand?.name || vendor_name,
          price: num(selected.discounted_cost),
          currency: String(selected.currency_type || "inr").toUpperCase(),
          signed_in: hasSession(),
        });
        if (hasSession()) return goToBrand(selected);
        // Signed in by the sheet, the click that opened it is spent, and a
        // tab opened now would be blocked: one more tap carries on.
        const plan = selected;
        const name = brand?.name || "the brand";
        return requireAuth(
          () =>
            setNotice({
              title: `Continue to ${name}`,
              body: `You'll sign up and pay on ${name}'s own site.`,
              dismissible: true,
              primary: {
                text: `Continue to ${name}`,
                onClick: () => {
                  setNotice(null);
                  goToBrand(plan);
                },
              },
            }),
          isIndia() ? `Verify your number to continue to ${name}.` : `Sign in to continue to ${name}.`
        );
      }

      if (vendor_name === "chess.com") {
        window.open(CHESS_AFFILIATE_URL, "_blank", "noopener,noreferrer");
        return;
      }

      const cartDetails = details || { subscription_plan: selected.subscription_plan_id, quantity: 1 };
      // An ordinary purchase: a gift written earlier for this plan must not
      // ride along to checkout.
      clearGiftDraft();

      if (!hasSession()) {
        localStorage.setItem("brandId", vendor_name);
        localStorage.setItem("returnToPage", window.location.pathname);
        // Outside India the sheet's own line names the store and Google; a number
        // to verify is India's step only.
        return requireAuth(() => buy(cartDetails), isIndia() ? "Verify your number to buy this plan." : undefined, {
          context: "buy",
        });
      }

      if (brand.activeSubscription) {
        setNotice({
          title: `You already have a recurring subscription for ${brand.name}.${brand.dueDate ? ` Your next purchase will be done on ${longDate(brand.dueDate)}.` : ""}`,
          dismissible: true,
          primary: { text: "Okay, got it", onClick: () => setNotice(null) },
        });
        return;
      }

      if (brand.extraDetails.length && !cartDetails.details) {
        setPendingDetails(cartDetails);
        setExtraOpen(true);
        return;
      }

      setBusy(true);
      try {
        const { default: Confetti } = await import("../../utils/ConfettiHelper");
        Confetti.smallBlast();
        await Api.EmptyCart(); // the cart holds a single plan
        await Api.AddToCart(cartDetails);
        track(EVENTS.CART_ITEM_ADDED, {
          plan_id: selected.subscription_plan_id ?? null,
          plan_name: selected.plan_name || null,
          brand: brand.name || vendor_name,
          price: num(selected.discounted_cost),
          currency: String(selected.currency_type || "inr").toUpperCase(),
          context: "plan_detail_buy",
        });
        Confetti.purchaseCelebration();
        navigate("/checkout");
      } catch (err) {
        console.error("Buy failed:", err);
        setBusy(false);
      }
    },
    [selected, busy, vendor_name, requireAuth, brand, navigate, goToBrand]
  );

  /* ---- Gift ---- */

  // A gift is one plan, in any store's currency, activated by whoever claims
  // the link. Plans that need the buyer's details at purchase can't be (the
  // server refuses them too), nor can a plan sold on the brand's own site. This was
  // rupee plans only until 2026-10-26; the server never limited gifts by
  // currency, and a gift in any currency but rupees pays through Stripe
  // Checkout like any other order outside India.
  const giftable = (p) =>
    !!p &&
    !isBlocked(p) &&
    !isAffiliate(p) &&
    vendor_name !== "chess.com" &&
    !brand?.extraDetails?.length;

  const openGiftSheet = useCallback(() => {
    track(EVENTS.GIFT_STARTED, {
      plan_id: selected?.subscription_plan_id ?? null,
      brand: brand?.name || vendor_name,
      price: num(selected?.discounted_cost),
      currency: String(selected?.currency_type || "inr").toUpperCase(),
    });
    setGiftError("");
    setGiftOpen(true);
  }, [selected, brand, vendor_name]);

  const startGift = useCallback(() => {
    if (!selected || giftBusy) return;
    const begin = async () => {
      setGiftBusy(true);
      // The sender's first name for the sheet, and what's in the cart now:
      // a gift is one plan on its own, so anything else there is replaced.
      const [profile, cart] = await Promise.all([
        Api.GetUserDetails().catch((e) => e),
        Api.GetCartItems().catch((e) => e),
      ]);
      setGiftBusy(false);
      if (!failed(profile)) setGiftSender(firstName(profile.data?.name));
      const items = !failed(cart) && Array.isArray(cart.data) ? cart.data : [];
      const others = items.filter(
        (i) =>
          String(i?.subscription_plan?.subscription_plan_id ?? i?.subscription_plan) !== String(selected.subscription_plan_id) ||
          num(i?.quantity || 1) !== 1
      );
      if (others.length) {
        setNotice({
          title: "Replace what's in your cart?",
          body: `A gift is one plan on its own, so your cart will hold only ${brand.name}${selected.plan_name ? ` ${selected.plan_name}` : ""}.`,
          dismissible: true,
          primary: {
            text: "Replace and continue",
            onClick: () => {
              setNotice(null);
              openGiftSheet();
            },
          },
          secondary: { text: "Keep my cart", onClick: () => setNotice(null) },
        });
        return;
      }
      openGiftSheet();
    };
    if (!hasSession()) {
      return requireAuth(begin, isIndia() ? "Verify your number to send a gift." : "Sign in to send a gift.", { context: "gift" });
    }
    begin();
  }, [selected, giftBusy, brand, requireAuth, openGiftSheet]);

  const continueGift = useCallback(
    async (gift) => {
      if (!selected || giftBusy) return;
      setGiftBusy(true);
      setGiftError("");
      try {
        // The same calls as Buy now: a one-plan cart, then checkout, which
        // places the order with the gift on it.
        await Api.EmptyCart();
        const added = await Api.AddToCart({ subscription_plan: selected.subscription_plan_id, quantity: 1 });
        if (failed(added)) {
          setGiftError(added?.response?.data?.error || "We couldn't start your gift. Try again.");
          setGiftBusy(false);
          return;
        }
        saveGiftDraft(selected.subscription_plan_id, gift);
        // Checkout reads a stored bundle id first; a gift is never a bundle.
        localStorage.removeItem("bundleId");
        track(EVENTS.CART_ITEM_ADDED, {
          plan_id: selected.subscription_plan_id ?? null,
          plan_name: selected.plan_name || null,
          brand: brand.name || vendor_name,
          price: num(selected.discounted_cost),
          currency: String(selected.currency_type || "inr").toUpperCase(),
          context: "plan_detail_gift",
        });
        navigate("/checkout");
      } catch (err) {
        console.error("Gift start failed:", err);
        setGiftError("We couldn't start your gift. Try again.");
        setGiftBusy(false);
      }
    },
    [selected, giftBusy, brand, vendor_name, navigate]
  );

  /* ---- Render ---- */

  if (errorDetails) return <ErrorFallback errorDetails={errorDetails} />;

  const title = brand?.name || "";
  const currency = selected?.currency_type;
  const blockedSelected = selected ? isBlocked(selected) : true;

  return (
    <div className={styles.page}>
      <TopBar title={title} backTo="/marketplace" />
      <NoticeSheet notice={notice} onClose={() => setNotice(null)} />
      <ExtraDetailsSheet
        open={extraOpen}
        fields={brand?.extraDetails || []}
        onClose={() => setExtraOpen(false)}
        onSubmit={(values) => {
          setExtraOpen(false);
          buy({ ...pendingDetails, details: values });
        }}
      />
      <GiftSheet
        open={giftOpen}
        planLabel={[brand?.name, selected?.plan_name].filter(Boolean).join(" ")}
        defaultSender={giftSender}
        busy={giftBusy}
        error={giftError}
        onClose={() => setGiftOpen(false)}
        onContinue={continueGift}
      />

      <div className={styles.shell}>
        {loading || !brand ? (
          <div className={styles.skel}>
            <div className={styles.skelBanner} />
            <div className={styles.skelBrand}>
              <div className={styles.skelLogo} />
              <div className={styles.skelLines}>
                <div className={styles.skelLine} />
                <div className={`${styles.skelLine} ${styles.skelLineShort}`} />
              </div>
            </div>
            <div className={styles.skelRow} />
            <div className={styles.skelRow} />
            <div className={styles.skelRow} />
          </div>
        ) : (
          <>
            <div className={styles.hero}>
              <div className={styles.banner}>
                {brand.banner ? (
                  <img
                    className={styles.bannerImg}
                    src={brand.banner}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.visibility = "hidden";
                    }}
                  />
                ) : (
                  <div className={styles.bannerImg} />
                )}
                {/* The same figure as the page title: the best saving on a plan
                    that can be bought now. */}
                {page?.maxPct > 0 && (
                  <span className={styles.bannerBadge}>
                    <span className={styles.bannerBadgeText}>UPTO {page.maxPct}% off</span>
                    <img src={coinLogo} alt="" className={styles.bannerBadgeIcon} />
                  </span>
                )}
              </div>

              <div className={styles.brandRow}>
                <img className={styles.logo} src={brand.logo || FleekIcon} alt="" onError={(e) => { e.currentTarget.src = FleekIcon; }} />
                <div className={styles.brandText}>
                  <div className={styles.brandNameRow}>
                    <h2 className={styles.brandName}>{brand.name}</h2>
                    {/* Every brand on the marketplace is a verified seller; the chip is
                        constant, as designed, rather than gated on a field the plan
                        endpoint doesn't return yet. */}
                    {(
                      <span className={styles.verified}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                        VERIFIED
                      </span>
                    )}
                    {bestsellerRank && <span className={styles.bestseller}>BESTSELLER</span>}
                  </div>
                  {brand.category && <div className={styles.category}>{brand.category}</div>}
                </div>
              </div>

              {brand.desc && <p className={styles.desc}>{brand.desc}</p>}
            </div>

            {/* ---- Choose a plan ---- */}
            <section className={styles.section}>
              <div className={styles.sectionHead}>
                <h3 className={styles.sectionTitle}>Choose a plan</h3>
                <span className={styles.sectionMeta}>{plans.length} {plans.length === 1 ? "option" : "options"}</span>
              </div>
              <div className={styles.plans} role="radiogroup" aria-label="Plans">
                {plans.map((p, i) => {
                  const off = isBlocked(p);
                  const on = i === selectedIdx && !off;
                  const pct = p.new_discount_percentage ?? (p.mrp_price ? Math.round((1 - num(p.discounted_cost) / num(p.mrp_price)) * 100) : 0);
                  const coinPct = num(p.coin_discount_percentage) || (num(p.coin_value_reserved) && num(p.discounted_cost) ? Math.round((num(p.coin_value_reserved) / num(p.discounted_cost)) * 100) : 0);
                  return (
                    <button
                      key={p.subscription_plan_id ?? i}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      disabled={off}
                      className={`${styles.planRow} ${on ? styles.planRowOn : ""} ${off ? styles.planRowOff : ""}`}
                      onClick={() => !off && setSelectedIdx(i)}
                    >
                      {(() => {
                        // The brand's own price: Fleek claims no saving on it.
                        if (isAffiliate(p)) return null;
                        const ad = adConfig
                          ? adCouponFor({ config: adConfig.config, code: adConfig.code, planId: p.subscription_plan_id, mrp: p.mrp_price, price: p.discounted_cost, currency: p.currency_type })
                          : null;
                        if (ad) return <span className={styles.planBadge}>{ad.code} · {money(ad.saving, p.currency_type)} off</span>;
                        // best_deal: the server marks the top plan when its discount clearly leads.
                        if (pct > 0 && p.best_deal) return <span className={styles.planBadge}>BEST DEAL · {pct}% OFF</span>;
                        return pct > 0 ? <span className={styles.planBadge}>{pct}% OFF</span> : null;
                      })()}
                      <span className={`${styles.dot} ${on ? styles.dotOn : ""}`}>{on && <span className={styles.dotInner} />}</span>
                      <span className={styles.planMain}>
                        <span className={styles.planName}>{p.plan_name}</span>
                        {(p.duration || p.duration_type) && (
                          <span className={styles.planMeta}>{[p.duration, p.duration_type].filter(Boolean).join(" ")}</span>
                        )}
                        {off && <span className={styles.planUnavailable}>{p.already_availed ? "Already availed" : "Currently unavailable"}</span>}
                      </span>
                      <span className={styles.planPrices}>
                        <span className={styles.planPrice}>{money(p.discounted_cost, p.currency_type)}</span>
                        {/* Outside India a service fee covers card processing
                            (backend marketplace.service_fee): in the price in
                            most stores, added at checkout in the rest. An MRP
                            shows only when the price is really below it. */}
                        {p.mrp_price && !isAffiliate(p) && !(num(p.service_fee) > 0 && num(p.mrp_price) <= num(p.discounted_cost)) ? (
                          <span className={styles.planMrp}>MRP {money(p.mrp_price, p.currency_type)}</span>
                        ) : null}
                        {num(p.service_fee) > 0 && (
                          <span className={styles.planMrp}>
                            {p.service_fee_in_price === false
                              ? `+ ${money(p.service_fee, p.currency_type)} service fee at checkout`
                              : `Incl. ${money(p.service_fee, p.currency_type)} service fee`}
                          </span>
                        )}
                        {coinPct > 0 && isIndia() && !isAffiliate(p) && (
                          <span className={styles.coinPill}>
                            <img src={coinLogo} alt="" className={styles.coinPillIcon} />
                            <span className={styles.coinPillText}>{coinPct}% extra off with coins</span>
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
              {isAffiliate(selected) && (
                <p className={styles.affiliateNote}>{affiliateNote(brand.name)}</p>
              )}
            </section>

            {/* ---- What's included ---- */}
            {brand.benefits.length > 0 && (
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>What's included</h3>
                <div className={styles.included}>
                  {brand.benefits.map((b, i) => (
                    <div className={styles.includedItem} key={i}>
                      <span className={styles.tick} aria-hidden="true">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      <span className={styles.includedText}>{b}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ---- How it works ---- */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>How it works</h3>
              <div className={styles.steps}>
                {(page?.steps ?? []).map((t, i) => (
                  <div className={styles.step} key={i}>
                    <span className={styles.stepN}>{i + 1}</span>
                    <span className={styles.stepText}>{t}</span>
                  </div>
                ))}
              </div>
              {/* How this plan is delivered today, from the live plan data. */}
              {selected?.activation_type_message && (
                <p className={styles.stepText}>{String(selected.activation_type_message).trim()}</p>
              )}
            </section>

            {/* ---- Questions ---- */}
            {page?.faq.length > 0 && (
              <section className={`${styles.section} ${styles.sectionLast}`}>
                <h3 className={styles.sectionTitle}>Questions</h3>
                <div className={styles.faqs}>
                  {page.faq.map((f, i) => (
                    <div className={styles.faq} key={i}>
                      <button type="button" className={styles.faqButton} onClick={() => setOpenFaq((o) => (o === i ? -1 : i))} aria-expanded={openFaq === i}>
                        <span className={styles.faqQ}>{f.question}</span>
                        <span className={styles.faqSign} aria-hidden="true">{openFaq === i ? "−" : "+"}</span>
                      </button>
                      {openFaq === i && <p className={styles.faqA}>{f.answer}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {related && (
              <section className={styles.section} aria-label="Related subscriptions">
                <h3 className={styles.sectionTitle}>More {related.category?.name || "subscriptions"} on Fleek</h3>
                {related.brands.length > 0 && (
                  <ul className={styles.relatedList}>
                    {related.brands.map((b) => (
                      <li key={b.slug}>
                        <Link className={styles.relatedLink} to={`/brand/${b.slug}`}>{b.name}</Link>
                      </li>
                    ))}
                  </ul>
                )}
                {related.category && (
                  <Link className={styles.relatedAll} to={`/category/${related.category.slug}`}>
                    All {related.category.name} subscriptions
                  </Link>
                )}
              </section>
            )}

            <CtaBar.Spacer />
            {selected && (
              <CtaBar
                label={isAffiliate(selected) ? `On ${brand.name}` : "Total"}
                amount={money(selected.discounted_cost, currency)}
                strike={selected.mrp_price && !isAffiliate(selected) ? money(selected.mrp_price, currency) : ""}
                text={
                  blockedSelected
                    ? "Unavailable"
                    : busy || !live
                      ? "Please wait…"
                      : isAffiliate(selected)
                        ? `Continue to ${brand.name}`
                        : "Buy now"
                }
                onClick={() => buy()}
                disabled={blockedSelected || busy || !live}
                secondary={
                  giftEnabled && giftable(selected)
                    ? { text: "Gift", icon: <GiftIcon />, onClick: startGift, disabled: busy || giftBusy || !live }
                    : null
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlanDetail;
